import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, X, FilmSlate, Presentation, FileArrowDown } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";

const iconFor = (cat) =>
  cat === "video" ? <FilmSlate size={18} /> : cat === "presentation" ? <Presentation size={18} /> : <FileArrowDown size={18} />;

export default function SearchOverlay({ open, onClose }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      api.get("/content").then((res) => setItems(res.data)).catch(() => {});
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const results = items
    .filter((i) => {
      const ql = q.toLowerCase();
      return (
        i.title.toLowerCase().includes(ql) ||
        (i.tags || []).some((tag) => tag.toLowerCase().includes(ql)) ||
        (i.speaker || "").toLowerCase().includes(ql)
      );
    })
    .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

  const go = (id) => {
    onClose();
    navigate(`/content/${id}`);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="search-overlay"
          className="fixed inset-0 z-[60] flex flex-col bg-ink-900/90 backdrop-blur-2xl"
        >
          <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-6 pt-28 md:pt-36">
            <div className="flex items-center justify-between border-b border-white/15 pb-4">
              <div className="flex flex-1 items-center gap-4">
                <MagnifyingGlass size={30} weight="light" className="text-cyan" />
                <input
                  ref={inputRef}
                  data-testid="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("search_placeholder")}
                  className="w-full bg-transparent font-head text-3xl font-light text-white outline-none placeholder:text-slate-600 md:text-5xl"
                />
              </div>
              <button data-testid="search-close" onClick={onClose} className="ml-4 text-slate-400 hover:text-white">
                <X size={30} weight="light" />
              </button>
            </div>

            <div className="mt-6 flex-1 overflow-y-auto pb-16">
              <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
                {results.length} {results.length === 1 ? "result" : "results"} · A–Z
              </p>
              {results.length === 0 && q && (
                <p className="font-body text-slate-500">{t("no_results")}</p>
              )}
              <div className="flex flex-col">
                {results.map((r, idx) => (
                  <motion.button
                    key={r.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    data-testid={`search-result-${r.id}`}
                    onClick={() => go(r.id)}
                    className="group flex items-center justify-between border-b border-white/5 py-4 text-left transition-colors hover:border-cyan/40"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-cyan opacity-70">{iconFor(r.category)}</span>
                      <div className="flex flex-col gap-1">
                        <span className="font-head text-lg text-slate-200 transition-colors group-hover:text-white md:text-xl">
                          {r.title}
                        </span>
                        {r.tags && r.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {r.tags.slice(0, 4).map((tag) => (
                              <span key={tag} className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.1em] text-slate-500">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600 group-hover:text-cyan">
                      {r.category}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
